import { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Filter, X, Megaphone, Calendar, Target, TrendingUp, Lock } from 'lucide-react';
import { Select } from '../components/ui/Select';
import { useLumisial } from '../context/LumisialContext';
import { campanaService } from '../services/campanaService';
import { Campaña } from '../lib/supabase';
import { CampanaModal } from '../components/CampanaModal';
import { formatCurrency, formatDate } from '../utils/formatters';
import { NivelOrigen, NIVEL_CONFIG } from '../utils/campanaConstants';
import { matchesSearch } from '../utils/textNormalization';
import { useAuth } from '../context/AuthContext';
import { puedeOperarDineroDeLumisial } from '../utils/permissions';
import { FormErrorAlert } from '../components/ui/FormErrorAlert';

interface CampanasPageProps {
  readonly onViewDetalle?: (campañaId: string) => void;
}

const ESTADO_BADGES: Record<string, { text: string; classes: string }> = {
  planificacion: { text: 'Planificación', classes: 'bg-blue-100 text-blue-700' },
  en_curso: { text: 'En Curso', classes: 'bg-emerald-100 text-emerald-700' },
  finalizada: { text: 'Finalizada', classes: 'bg-slate-100 text-slate-600' },
  archivada: { text: 'Archivada', classes: 'bg-slate-100 text-slate-400' },
};

const NIVEL_GRUPOS: Array<{ nivel: NivelOrigen; titulo: string; subtitulo: string }> = [
  { nivel: 'global', titulo: 'Internacional', subtitulo: 'Campañas distribuidas desde la coordinación internacional' },
  { nivel: 'nacional', titulo: 'Nacional', subtitulo: 'Campañas distribuidas desde la coordinación nacional' },
  { nivel: 'diocesano', titulo: 'Diocesana / Prefectura', subtitulo: 'Campañas distribuidas desde la diócesis o prefectura' },
  { nivel: 'local', titulo: 'Local', subtitulo: 'Campañas creadas en esta sede' },
];


interface CampanaCardProps {
  readonly campaña: Campaña;
  readonly onClick: () => void;
}

const CampanaCard: React.FC<CampanaCardProps> = ({ campaña, onClick }) => {
  const progreso = campaña.objetivo_recaudacion > 0
    ? Math.min((campaña.monto_recaudado / campaña.objetivo_recaudacion) * 100, 100)
    : 0;

  const nivel = (campaña.nivel_origen || 'local') as NivelOrigen;
  const nivelConf = NIVEL_CONFIG[nivel] || NIVEL_CONFIG.local;
  const estadoBadge = ESTADO_BADGES[campaña.estado] || ESTADO_BADGES.planificacion;

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left bg-white border border-slate-200 rounded-xl p-5 hover:shadow-lg hover:border-slate-300 transition-all cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${nivelConf.badgeClass}`}>
              {nivelConf.icon}
              {nivelConf.label}
            </span>
            {campaña.objetivo_bloqueado && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-600 border border-amber-200">
                <Lock className="w-3 h-3" />
                Objetivo fijo
              </span>
            )}
          </div>
          <h3 className="text-base font-bold text-slate-800 group-hover:text-sky-600 transition-colors leading-tight">
            {campaña.nombre}
          </h3>
        </div>
        <span className={`ml-3 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide flex-shrink-0 ${estadoBadge.classes}`}>
          {estadoBadge.text}
        </span>
      </div>

      {campaña.descripcion && (
        <p className="text-sm text-slate-500 mb-3 line-clamp-2">{campaña.descripcion}</p>
      )}

      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
          <span>
            {formatDate(campaña.fecha_inicio)}
            {campaña.fecha_fin && ` — ${formatDate(campaña.fecha_fin)}`}
          </span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1.5 text-slate-500">
            <Target className="w-3.5 h-3.5" />
            <span>Objetivo: {formatCurrency(campaña.objetivo_recaudacion)}</span>
          </div>
          <div className="flex items-center gap-1.5 font-semibold text-emerald-600">
            <TrendingUp className="w-3.5 h-3.5" />
            <span>{formatCurrency(campaña.monto_recaudado)}</span>
          </div>
        </div>
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-500">Progreso</span>
          <span className="font-bold text-slate-700">{progreso.toFixed(0)}%</span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-500 rounded-full"
            style={{ width: `${progreso}%` }}
          />
        </div>
      </div>
    </button>
  );
};

export const CampanasPage: React.FC<CampanasPageProps> = ({ onViewDetalle }) => {
  const { selectedLumisial } = useLumisial();
  const { usuario } = useAuth();
  const puedeCrear = puedeOperarDineroDeLumisial(usuario, selectedLumisial);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  const [campañas, setCampañas] = useState<Campaña[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedCampaña, setSelectedCampaña] = useState<Campaña | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [showFilters, setShowFilters] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState<string>('');

  const lumisialId = selectedLumisial?.id;
  const consultar = useCallback(() => lumisialId ? campanaService.getByLumisial(lumisialId)
    : Promise.resolve([]), [lumisialId]);
  const recibir = useCallback((datos: Campaña[]) => {
    setCampañas(datos); setErrorCarga(null); setLoading(false);
  }, []);
  const fallo = useCallback(() => { setErrorCarga('No pudimos consultar las campañas.'); setLoading(false); }, []);
  useEffect(() => {
    let vigente = true;
    consultar().then(datos => { if (vigente) recibir(datos); }).catch(() => { if (vigente) fallo(); });
    return () => { vigente = false; };
  }, [consultar, recibir, fallo]);
  const loadCampañas = () => { setLoading(true); void consultar().then(recibir).catch(fallo); };

  const handleCreate = () => {
    if (!puedeCrear) return;
    setSelectedCampaña(null);
    setModalMode('create');
    setShowModal(true);
  };

  const resetFilters = () => {
    setBusqueda('');
    setEstadoFiltro('');
  };

  const hasActiveFilters = !!(busqueda || estadoFiltro);

  const campañasFiltradas = campañas.filter(c => {
    const matchBusqueda = !busqueda ||
      matchesSearch(c.nombre, busqueda) ||
      matchesSearch(c.descripcion, busqueda);
    const matchEstado = !estadoFiltro || c.estado === estadoFiltro;
    return matchBusqueda && matchEstado;
  });

  const gruposConCampañas = NIVEL_GRUPOS
    .map(g => ({
      ...g,
      campañas: campañasFiltradas.filter(c => (c.nivel_origen || 'local') === g.nivel),
    }))
    .filter(g => g.campañas.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800 mb-1">Campañas</h2>
          <p className="text-slate-500">Campañas de recaudación de la sede</p>
        </div>
        {puedeCrear && <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-sky-600 text-white font-semibold rounded-lg hover:bg-sky-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nueva Campaña
        </button>}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por nombre o descripción..."
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 text-sm"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-colors font-semibold text-sm ${
              showFilters || hasActiveFilters
                ? 'bg-sky-600 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filtros
            {hasActiveFilters && <span className="bg-white/30 px-1.5 py-0.5 rounded text-xs">Activos</span>}
          </button>
        </div>

        {showFilters && (
          <div className="bg-slate-50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Select
                  label="Estado"
                  options={[
                    { value: 'planificacion', label: 'Planificación' },
                    { value: 'en_curso', label: 'En Curso' },
                    { value: 'finalizada', label: 'Finalizada' },
                  ]}
                  value={estadoFiltro}
                  onChange={(v) => setEstadoFiltro(v)}
                  placeholder="Todos"
                />
              </div>
            </div>
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="mt-3 flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 font-medium"
              >
                <X className="w-4 h-4" />
                Limpiar filtros
              </button>
            )}
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" />
          </div>
        )}
        <FormErrorAlert error={errorCarga} />
        {errorCarga && <button onClick={loadCampañas}>Reintentar consulta</button>}
        {!loading && !errorCarga && campañasFiltradas.length === 0 && (
          <div className="text-center py-16">
            <Megaphone className="w-14 h-14 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-medium mb-1">No hay campañas registradas</p>
            {hasActiveFilters
              ? <p className="text-sm text-slate-400">Intenta cambiar los filtros</p>
              : puedeCrear && <button onClick={handleCreate} className="mt-3 text-sky-600 hover:underline text-sm font-medium">
                  Crea la primera campaña
                </button>
            }
          </div>
        )}
        {!loading && campañasFiltradas.length > 0 && (
          <div className="space-y-8">
            {gruposConCampañas.map(grupo => (
              <div key={grupo.nivel}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    {NIVEL_CONFIG[grupo.nivel].icon && (
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${NIVEL_CONFIG[grupo.nivel].badgeClass}`}>
                        {NIVEL_CONFIG[grupo.nivel].icon}
                        {NIVEL_CONFIG[grupo.nivel].label}
                      </span>
                    )}
                    <h3 className="text-base font-bold text-slate-700">{grupo.titulo}</h3>
                    <span className="text-xs text-slate-400 font-medium bg-slate-100 px-2 py-0.5 rounded-full">
                      {grupo.campañas.length}
                    </span>
                  </div>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <p className="text-xs text-slate-400 mb-4 -mt-2">{grupo.subtitulo}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {grupo.campañas.map(campaña => (
                    <CampanaCard
                      key={campaña.id}
                      campaña={campaña}
                      onClick={() => onViewDetalle && onViewDetalle(campaña.id)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <CampanaModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setSelectedCampaña(null); }}
        onSuccess={loadCampañas}
        campaña={selectedCampaña || undefined}
        lumisialId={selectedLumisial?.id || ''}
        mode={modalMode}
      />
    </div>
  );
};
